/**
 * What this release offers.
 *
 * Phase 1 is a staff-operated, cash-only booking system. WhatsApp and the
 * in-app support chat are both out of it, so their entry points are switched
 * off rather than deleted — the code stays whole and comes back by setting one
 * variable when the feature does.
 *
 * These decide what the screens *offer*. The server refuses the same things
 * regardless of what is drawn here, which is what actually closes them: hiding
 * a button never stopped anyone from sending the request by hand.
 */

/** Anything reading `process.env` in CRA needs the literal, not a computed key. */
const on = (value: string | undefined) => value === "true";

/**
 * Is WhatsApp offered anywhere?
 *
 * Off for phase 1. Covers the floating WhatsApp button; the bot itself is
 * refused at the server's webhook, since the number is public and hiding a
 * button on the website does not stop anyone messaging it.
 */
export const whatsappEnabled = on(process.env.REACT_APP_WHATSAPP_ENABLED);

/**
 * Is the in-app support chat running?
 *
 * Off for phase 1. One switch covers the customer's chat bubble, the agent's
 * Support Inbox and the socket behind both — leaving the inbox reachable while
 * customers cannot write to it would give staff an empty screen to watch.
 */
export const supportChatEnabled = on(process.env.REACT_APP_SUPPORT_CHAT_ENABLED);

/**
 * Is the "Sales Reports" menu shown in the navbar?
 *
 * Off by default. Hides the desktop and mobile entry points only; the report
 * screens and the server's `reports` module are untouched, so it comes back by
 * setting one variable.
 */
export const salesReportsEnabled = on(process.env.REACT_APP_SALES_REPORTS_ENABLED);

/**
 * Is the "Content" (Content Studio) menu shown in the navbar?
 *
 * Off by default. Hides the desktop and mobile entry points only; the Content
 * Studio screen and the server's `content` module stay as they are.
 */
export const contentMenuEnabled = on(process.env.REACT_APP_CONTENT_MENU_ENABLED);

/**
 * Does the home screen offer anything besides flights?
 *
 * Off by default. Phase 1 sells flights, so the Hotels, Visas, Holidays,
 * Offers and More tabs are hidden rather than deleted — the hotel search bar,
 * the visa screen and their routes are all untouched and come back by setting
 * one variable.
 *
 * Only the tabs go. With them hidden the home screen cannot leave the flights
 * tab, so the flight search bar is what renders; nothing else on the page
 * changes.
 */
export const homeNonFlightTabsEnabled = on(process.env.REACT_APP_HOME_NON_FLIGHT_TABS_ENABLED);

/**
 * Are Google and phone-number sign-in offered?
 *
 * Off by default, and hidden rather than deleted — the handlers, the phone
 * OTP screen and the reCAPTCHA verifier all stay wired up.
 *
 * Neither fits how accounts work in phase 1. Google never hands over a phone
 * number, and the sign-in code is sent to one; a phone-only account carries no
 * email address at all, so it can never satisfy the company-domain rule that
 * decides who may register. Offering either sends somebody through a popup or
 * a real SMS only to be refused at the end.
 */
export const socialSignInEnabled = on(process.env.REACT_APP_SOCIAL_SIGN_IN_ENABLED);

/**
 * May a booked ticket be cancelled, refunded or exchanged from My Trips?
 *
 * Off. Mirrors TICKET_CHANGES_ENABLED on the server, which is the thing that
 * actually closes it — /cancelBooking and /flights/exchange/* refuse whatever
 * this says, because both spend real money at Sabre and a hidden button stops
 * nobody who can replay a request.
 *
 * Hides the two buttons only. The cancel and exchange modals, their API
 * helpers and the cancellation OTP flow are all left whole and come back by
 * setting one variable.
 */
export const ticketChangesEnabled = on(process.env.REACT_APP_TICKET_CHANGES_ENABLED);

/**
 * May a booked ticket be exchanged (date change) from My Trips?
 *
 * Off, and split from ticketChangesEnabled because the two are not ready
 * together. Cancelling and voiding work today; a reissue moves money —
 * add-collect, residual value, penalties — and the exchange payloads have never
 * been validated against a live Sabre response. Sharing one switch would mean
 * drawing an Exchange button that always fails in order to draw a Cancel button
 * that works.
 *
 * The server refuses /flights/exchange/* regardless, via SABRE_EXCHANGES_ENABLED.
 */
export const exchangeEnabled = on(process.env.REACT_APP_EXCHANGE_ENABLED);

/**
 * Is "Refund" offered as a cancellation type?
 *
 * Off. Void and segment cancellation are safe to offer — both are reversible
 * within the day and cost nothing. A refund moves money out and depends on an
 * authority Sabre has not confirmed for this PCC, so the option is left out of
 * the modal rather than failing at the provider with a customer waiting.
 *
 * Mirrors REFUND_ENABLED on the server, which is what actually refuses it.
 */
export const refundEnabled = on(process.env.REACT_APP_REFUND_ENABLED);

/**
 * Are the frequent-flyer fields offered on the passenger screen?
 *
 * Off. The two fields go together — the host entry is FF<carrier><number> and
 * Sabre checks the pair against that carrier's records, so a number without an
 * airline cannot be recorded — which is why one switch hides both.
 *
 * Hidden, not removed: anything already stored on a passenger is still sent,
 * and the frequent-flyer code on the server is untouched.
 */
export const frequentFlyerEnabled = on(process.env.REACT_APP_FREQUENT_FLYER_ENABLED);
